export const fmtDate = (input?: string | number): string => {
    if (input == null || input === '') return '—';
    const d = typeof input === 'number' ? new Date(input) : new Date(input);
    if (Number.isNaN(d.getTime())) return String(input);
    return d.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: '2-digit' });
};

// Format a year-month value as "Aug 2025". Accepts:
//   - "2025-08", "2025-8", "2025/08"     (YYYY-M / YYYY/M)
//   - "202508"                            (YYYYMM, no separator)
//   - 202508                              (numeric YYYYMM)
//   - any Date-parsable string ("2025-08-01", ISO timestamp, etc.)
//
// Falls back to the raw input only if nothing parses. The YYYYMM-without-
// separator path matters because the API ships month strings that way for the
// employee headcount series — without this branch the renderer used to print
// the literal "as of 202508" in place of "as of Aug 2025".
const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatYearMonth = (year: number, monthIdx: number): string => `${MONTH_LABELS[Math.max(0, Math.min(11, monthIdx))]} ${year}`;

export const fmtMonthYear = (input?: string | number): string => {
    if (input == null || input === '') return '—';
    const s = String(input).trim();
    if (!s) return '—';

    // YYYYMM without separators — six pure digits, last two are 1-12.
    const compact = s.match(/^(\d{4})(\d{2})$/);
    if (compact) {
        const month = parseInt(compact[2], 10);
        if (month >= 1 && month <= 12) return formatYearMonth(parseInt(compact[1], 10), month - 1);
    }

    // YYYY-MM / YYYY/MM — anchor at start so a full ISO date ("2025-08-19") falls through.
    const dashed = s.match(/^(\d{4})[-/](\d{1,2})$/);
    if (dashed) {
        const month = parseInt(dashed[2], 10);
        if (month >= 1 && month <= 12) return formatYearMonth(parseInt(dashed[1], 10), month - 1);
    }

    const d = typeof input === 'number' ? new Date(input) : new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleDateString('en-IN', { year: 'numeric', month: 'short' });
};

// Compact "Aug'25" form for chart axis labels where horizontal space is
// expensive. Same input acceptance as fmtMonthYear; falls back to raw input.
export const fmtMonthYearShort = (input?: string | number): string => {
    const formatted = fmtMonthYear(input);
    if (formatted === '—' || formatted === String(input)) return formatted;
    // formatted = "Aug 2025"
    const m = formatted.match(/^([A-Za-z]+) (\d{4})$/);
    if (!m) return formatted;
    return `${m[1]}'${m[2].slice(2)}`;
};

export const fmtNum = (n?: number | null, digits = 2): string => {
    if (n == null || Number.isNaN(n)) return '—';
    return Number(n).toLocaleString('en-IN', { maximumFractionDigits: digits });
};

export const fmtPct = (n?: number | null, digits = 1): string => {
    if (n == null || Number.isNaN(n)) return '—';
    return `${Number(n).toFixed(digits)}%`;
};

export const fmtAmtCr = (n?: number | null): string => {
    if (n == null || Number.isNaN(n)) return '—';
    return `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })} Cr`;
};

export const tenureFrom = (start?: number): string => {
    if (!start) return '—';
    const ms = Date.now() - start;
    const years = ms / (365.25 * 24 * 3600 * 1000);
    if (years < 1) {
        const months = Math.max(1, Math.round(years * 12));
        return `${months}m`;
    }
    return `${years.toFixed(1)}y`;
};

export const truncate = (s?: string | null, n = 140): string => {
    if (!s) return '—';
    return s.length > n ? s.slice(0, n - 1) + '…' : s;
};
